import { usePricing } from '../pricing';

const FREE = [
  'AIO Readiness score + full findings',
  'Visibility probe, up to 5 questions',
  'Footprint (cold vs warm), up to 5 questions',
  'Honest recommendations & tool links',
];

const PRO = [
  'Everything in Free',
  'Visibility and Footprint',
  'The full Fix it action plan',
  'Draft FAQ answers from your content',
  'History, trends & re-scans',
  'All AI models & the visibility probe, unlimited (fair use)',
];

function Check(): JSX.Element {
  return <span className="text-cyan">✓</span>;
}

export default function Pricing({ plan, onGoPro }: { plan: 'free' | 'pro'; onGoPro: () => void }): JSX.Element {
  const pricing = usePricing();
  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col gap-6">
      <div className="text-center">
        <h2 className="font-serif text-2xl mb-1">Pricing</h2>
        <p className="text-text-secondary text-sm">
          One simple plan. No content mill, no “we'll get you mentioned” promises, just a tool you use yourself to improve
          your own AI visibility.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Free */}
        <div className="border border-warm-dark-2 rounded-xl p-5">
          <p className="font-mono text-sm uppercase tracking-widest text-text-secondary">Free test</p>
          <p className="font-serif text-3xl mt-1 mb-3">€0</p>
          <ul className="space-y-2 text-sm">
            {FREE.map((f) => (
              <li key={f} className="flex gap-2 text-text-secondary">
                <Check /> {f}
              </li>
            ))}
            <li className="flex gap-2 text-text-secondary/60">
              <span className="text-text-secondary/40">✕</span> Fix it action plan (locked)
            </li>
          </ul>
          <p className="mt-4 font-mono text-sm text-text-secondary">
            {plan === 'free' ? 'Your current plan' : 'Free plan'}
          </p>
        </div>

        {/* Pro */}
        <div className="border-2 border-cyan rounded-xl p-5 bg-cyan/5">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-mono text-sm uppercase tracking-widest text-cyan">Pro, everything</p>
            {pricing.promo.active && pricing.promo.badge && (
              <span className="font-mono text-xs uppercase tracking-wide text-warm-dark bg-cyan rounded px-1.5 py-0.5">
                {pricing.promo.badge}
              </span>
            )}
          </div>
          <p className="font-serif text-3xl mt-1 mb-1">
            €{pricing.monthlyEur}<span className="text-base text-text-secondary font-sans"> / month</span>
          </p>
          {pricing.promo.active && pricing.promo.note && (
            <p className="text-sm text-cyan/80 mb-3">{pricing.promo.note}</p>
          )}
          {!(pricing.promo.active && pricing.promo.note) && <div className="mb-3" />}
          <ul className="space-y-2 text-sm">
            {PRO.map((f) => (
              <li key={f} className="flex gap-2 text-text-primary">
                <Check /> {f}
              </li>
            ))}
          </ul>
          {plan === 'pro' ? (
            <p className="mt-4 font-mono text-sm text-measured">✓ Pro active</p>
          ) : (
            <>
              <button
                onClick={onGoPro}
                className="mt-4 w-full bg-cyan text-warm-dark font-medium rounded-lg px-6 py-2.5 text-sm hover:opacity-90 transition"
              >
                Go Pro, €{pricing.monthlyEur}/mo
              </button>
              <p className="mt-2 font-mono text-sm text-text-secondary text-center">
                (ordering emails you an invoice; Pro activates once payment is confirmed)
              </p>
            </>
          )}
        </div>
      </div>

      {/*
        RAG Chat bundle.

        This used to read "Pro is included free with RAG Chat", with no end to it.
        The offer now attaches to a six month RAG Chat signup and runs for one
        month here, so the copy has to say both halves out loud: what you get,
        and what happens when the month is over. A free month that quietly turns
        into a charge is the kind of thing this whole product is positioned
        against.
      */}
      <div className="border-2 border-cyan/50 rounded-xl p-5 bg-cyan/5 text-center">
        <p className="font-mono text-sm uppercase tracking-widest text-cyan mb-1">Signing up for RAG Chat?</p>
        <p className="font-serif text-xl mb-2">Take RAG Chat for six months and your first month of Pro here is free</p>
        <p className="text-sm text-text-secondary mb-4">
          RAG Chat makes AI answer <em>on</em> your site; AI Visibility Scan makes AI cite you <em>everywhere else</em>. Two
          halves of the same visibility loop, so a RAG Chat signup of six months or more comes with a month of Pro on this
          side, no extra charge. After that month Pro is €{pricing.monthlyEur} a month if you want to keep it, and the free
          scan stays free whether you do or not. Nothing starts charging you on its own.
        </p>
        <a
          href="https://pagecoder.ai/products/rag-chat/"
          target="_blank"
          rel="noreferrer"
          className="inline-block bg-cyan text-warm-dark font-medium rounded-lg px-6 py-2.5 text-sm hover:opacity-90 transition"
        >
          Get PageCoder RAG Chat ↗
        </a>
      </div>

      <p className="text-sm text-text-secondary text-center border-t border-warm-dark-2 pt-4">
        EU-based billing. Cancel anytime. Everything here is something you can also do yourself for free.
        Pro just does the heavy lifting and gives you the full plan.
      </p>
    </div>
  );
}
